import React, { useState } from "react"
import {
	SwipeableDrawer,
	Grid,
	Typography,
	Box,
	IconButton
} from "@material-ui/core"
import {
	ArrowBackIosOutlined as ArrowBackIcon
} from "@material-ui/icons"
import {
	Settings as SettingsIcon,
	Gift as GiftIcon,
	HelpCircle as HelpCircleIcon,
	ChevronRight as ChevronRightIcon,
	User as UserIcon
} from "lucide-react"

import { useGlobalStateStore } from "@/store/GlobalState"
import { letalkLinks } from "@/utils/link"
import InstanceSelectorItem from "@/@integrations/Whatsapp/components/Menu/NewSideBar/InstanceSelectorItem"
import useProfileMenuItems from "@/hooks/useProfileMenuItems"

import useStyles from "@/pages/Attendance/MobileBottomTabBar/MoreSheet/styles"

type MoreSheetProps = {
	open: boolean
	onClose: () => void
	onUserSettingsOpen: () => void
	onReferAndEarnClick: () => void
}

type MenuItem = {
	icon: React.ReactNode
	label: string
	onClick: () => void
}

const PROFILE_AVATAR_ICON_SIZE_IN_PIXELS = 20
const CHEVRON_ICON_SIZE_IN_PIXELS = 20
const MENU_ITEM_ICON_SIZE_IN_PIXELS = 22

const MoreSheet: React.FC<MoreSheetProps> = (props) => {
	const { open, onClose, onUserSettingsOpen, onReferAndEarnClick } = props

	const classes = useStyles()
	const globalStateStore = useGlobalStateStore()
	const { profileMenuAccountItems } = useProfileMenuItems()

	const [isProfileSubSheetOpen, setIsProfileSubSheetOpen] = useState(false)

	const userName = globalStateStore.user?.name ?? ""
	const userEmail = globalStateStore.user.email

	const handleUserSettingsClick = () => {
		onClose()
		onUserSettingsOpen()
	}

	const handleReferAndEarnClick = () => {
		onClose()
		onReferAndEarnClick()
	}

	const handleHelpClick = () => {
		onClose()
		window.open(letalkLinks.wikiMainPage, "_blank")
	}

	const handleProfileRowClick = () => {
		setIsProfileSubSheetOpen(true)
	}

	const handleBackToMainSheet = () => {
		setIsProfileSubSheetOpen(false)
	}

	const handleProfileSubSheetItemClick = (itemOnClick: () => void) => {
		onClose()
		itemOnClick()
	}

	const handleDrawerClose = () => {
		setIsProfileSubSheetOpen(false)
		onClose()
	}

	// eslint-disable-next-line @typescript-eslint/no-empty-function -- SwipeableDrawer requires onOpen; sheet is opened programmatically, never by gesture
	const handleSheetOpen = () => {}

	const menuItems: MenuItem[] = [
		{
			icon: <SettingsIcon size={MENU_ITEM_ICON_SIZE_IN_PIXELS} />,
			label: "Configurações",
			onClick: handleUserSettingsClick
		},
		{
			icon: <GiftIcon size={MENU_ITEM_ICON_SIZE_IN_PIXELS} />,
			label: "Indique e Ganhe",
			onClick: handleReferAndEarnClick
		},
		{
			icon: <HelpCircleIcon size={MENU_ITEM_ICON_SIZE_IN_PIXELS} />,
			label: "Preciso de ajuda",
			onClick: handleHelpClick
		}
	]

	return (
		<SwipeableDrawer
			anchor="bottom"
			open={open}
			onClose={handleDrawerClose}
			onOpen={handleSheetOpen}
			TransitionProps={{ timeout: 280 }}
			BackdropProps={{
				className: classes.scrimBackground,
				transitionDuration: 220
			}}
			PaperProps={{ className: classes.sheetPaper }}
			ModalProps={{ style: { zIndex: 1200 } }}
		>
			<Grid container direction="column">
				<Grid item>
					<Box className={classes.dragHandle} />
				</Grid>

				{isProfileSubSheetOpen ? (
					<>
						<Grid item>
							<Grid
								container
								alignItems="center"
								className={classes.subSheetHeader}
							>
								<Grid item xs={4}>
									<IconButton onClick={handleBackToMainSheet}>
										<ArrowBackIcon />
									</IconButton>
								</Grid>
								<Grid item xs={4}>
									<Grid container justifyContent="center">
										<Grid item>
											<Typography className={classes.subSheetTitle}>
												Meus dados
											</Typography>
										</Grid>
									</Grid>
								</Grid>
								<Grid item xs={4} />
							</Grid>
						</Grid>

						<Grid item>
							<Grid
								container
								direction="column"
							>
								{profileMenuAccountItems.map((item) => (
									<Grid
										key={item.label}
										item
										className={classes.menuItem}
										onClick={() => handleProfileSubSheetItemClick(item.onClick)}
									>
										<Grid container alignItems="center">
											<Grid item>
												<Box className={classes.menuItemIcon}>
													{item.icon}
												</Box>
											</Grid>
											<Grid item>
												<Typography className={classes.menuItemLabel}>
													{item.label}
												</Typography>
											</Grid>
										</Grid>
									</Grid>
								))}
							</Grid>
						</Grid>
					</>
				) : (
					<>
						<Grid item>
							<Typography className={classes.moreLabel}>
							Mais
							</Typography>
						</Grid>

						<Grid item>
							<Grid
								container
								direction="column"
							>
								{menuItems.map((item) => (
									<Grid
										key={item.label}
										item
										className={classes.menuItem}
										onClick={item.onClick}
									>
										<Grid container alignItems="center">
											<Grid item>
												<Box className={classes.menuItemIcon}>
													{item.icon}
												</Box>
											</Grid>
											<Grid item>
												<Typography className={classes.menuItemLabel}>
													{item.label}
												</Typography>
											</Grid>
										</Grid>
									</Grid>
								))}
							</Grid>
						</Grid>

						<Grid item onClick={onClose}>
							<InstanceSelectorItem isSidebarOpen />
						</Grid>

						<Grid item>
							<Box className={classes.profileDivider} />
						</Grid>

						<Grid item>
							<Box
								className={classes.profileRow}
								onClick={handleProfileRowClick}
							>
								<Grid container alignItems="center">
									<Grid item>
										<Grid
											container
											alignItems="center"
											justifyContent="center"
											className={classes.profileAvatar}
										>
											<Grid item>
												<UserIcon
													size={PROFILE_AVATAR_ICON_SIZE_IN_PIXELS}
													className={classes.profileAvatarIcon}
												/>
											</Grid>
										</Grid>
									</Grid>
									<Grid item xs>
										<Grid
											container
											direction="column"
											className={classes.profileTextContainer}
										>
											<Grid item>
												<Typography className={classes.profileName}>
													{userName}
												</Typography>
											</Grid>
											<Grid item>
												<Typography className={classes.profileEmail}>
													{userEmail}
												</Typography>
											</Grid>
										</Grid>
									</Grid>
									<Grid item>
										<ChevronRightIcon
											size={CHEVRON_ICON_SIZE_IN_PIXELS}
											className={classes.profileChevron}
										/>
									</Grid>
								</Grid>
							</Box>
						</Grid>
					</>
				)}
			</Grid>
		</SwipeableDrawer>
	)
}

export default MoreSheet
